import React, { useEffect, useRef, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { ImportRowResult } from '../../../interfaces/openingBalance.interface';
import { TEMPLATES, downloadTemplate } from '../../../utils/csvImport';
import { useToast } from '../../../context/ToastContext';
import { formatMoney } from '../../../utils/format';

interface ResultShape { committed: boolean; errorCount: number; rows: ImportRowResult[]; errors?: string[]; warnings?: string[] }

export interface OpeningImportPanelProps<TRow, TResult extends ResultShape> {
    templateKey: keyof typeof TEMPLATES;
    description: React.ReactNode;
    parse: (text: string) => { rows: TRow[]; missing: string[] };
    run: (rows: TRow[], dryRun: boolean) => Promise<TResult>;
    /** Why the preview/post buttons are disabled (e.g. no go-live date yet); empty = enabled. */
    blockedReason?: string;
    /** Changing this invalidates a previous preview (go-live date, offset checkbox ...). */
    inputsKey?: string;
    /** Controls rendered above the file picker (go-live date etc.). */
    controls?: React.ReactNode;
    /** Result summary under the table (totals, journal date ...). */
    renderSummary?: (result: TResult) => React.ReactNode;
    previewLabel?: string;
    postLabel: string;
    confirmText: string;
    amountColumns?: 'debitCredit' | 'amount' | 'none';
    onCommitted?: (result: TResult) => void;
}

const statusColor = (s: string): 'success' | 'default' | 'error' =>
    s === 'CREATE' ? 'success' : s === 'EXISTS' ? 'default' : 'error';

/** Upload/paste CSV -> dry-run preview with per-row errors -> explicit post. Shared by every opening-balance importer. */
function OpeningImportPanel<TRow, TResult extends ResultShape>(props: OpeningImportPanelProps<TRow, TResult>) {
    const {
        templateKey, description, parse, run, blockedReason, inputsKey, controls, renderSummary,
        previewLabel = 'Preview (dry run)', postLabel, confirmText, amountColumns = 'none', onCommitted,
    } = props;
    const toast = useToast();
    const fileRef = useRef<HTMLInputElement>(null);
    const [text, setText] = useState('');
    const [fileName, setFileName] = useState('');
    const [parseError, setParseError] = useState('');
    const [rows, setRows] = useState<TRow[]>([]);
    const [preview, setPreview] = useState<TResult | null>(null);
    const [busy, setBusy] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [done, setDone] = useState<TResult | null>(null);

    // Any change to the file or to the inputs makes the previous preview stale.
    useEffect(() => { setPreview(null); setDone(null); }, [text, inputsKey]);

    const handleText = (value: string) => {
        setText(value);
        if (!value.trim()) { setRows([]); setParseError(''); return; }
        const parsed = parse(value);
        if (parsed.missing.length > 0) {
            setRows([]);
            setParseError(`Missing column(s): ${parsed.missing.join(', ')}. Download the template to see the expected headers.`);
        } else if (parsed.rows.length === 0) {
            setRows([]);
            setParseError('No data rows found under the header.');
        } else {
            setRows(parsed.rows);
            setParseError('');
        }
    };

    const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name);
        handleText(await file.text());
        e.target.value = '';
    };

    const runPreview = async () => {
        setBusy(true);
        try {
            setPreview(await run(rows, true));
        } catch (err) {
            toast.showApiError(err, 'Preview failed');
        } finally {
            setBusy(false);
        }
    };

    const commit = async () => {
        setConfirmOpen(false);
        setBusy(true);
        try {
            const result = await run(rows, false);
            setDone(result);
            setPreview(null);
            toast.showSuccess('Opening balances posted');
            onCommitted?.(result);
        } catch (err) {
            toast.showApiError(err, 'Posting failed');
        } finally {
            setBusy(false);
        }
    };

    const shown = done ?? preview;
    const canPost = !!preview && preview.errorCount === 0 && !busy && !blockedReason;

    return (
        <Box data-help-section="accounting.openingImport.panel" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">{description}</Typography>
            {controls}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                <Button data-help="accounting.openingImport.template" variant="outlined" startIcon={<DownloadIcon />} onClick={() => downloadTemplate(templateKey)}>
                    Download CSV template
                </Button>
                <Button data-help="accounting.openingImport.choose" variant="outlined" startIcon={<UploadFileIcon />} onClick={() => fileRef.current?.click()}>
                    Choose CSV file
                </Button>
                <input ref={fileRef} type="file" accept=".csv,text/csv,.txt" hidden data-testid="csv-file-input" onChange={handleFile} />
                {fileName && <Typography variant="body2">{fileName}</Typography>}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                <TextField data-help="accounting.openingImport.paste"
                    label="...or paste CSV here (with header row)"
                    multiline minRows={3} maxRows={10} value={text}
                    onChange={e => handleText(e.target.value)}
                    sx={{ flex: '1 1 320px', maxWidth: 900 }}
                    slotProps={{ htmlInput: { 'aria-label': 'csv text' } }}
                />
            </Box>
            {parseError && <Alert severity="error">{parseError}</Alert>}
            {rows.length > 0 && !parseError && (
                <Typography variant="body2">{rows.length} row(s) read from the file.</Typography>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                <Button data-help="accounting.openingImport.preview" variant="contained" onClick={runPreview} disabled={busy || rows.length === 0 || !!blockedReason}>
                    {previewLabel}
                </Button>
                <Button data-help="accounting.openingImport.post" variant="contained" color="success" onClick={() => setConfirmOpen(true)} disabled={!canPost}>
                    {postLabel}
                </Button>
                {busy && <CircularProgress size={22} />}
                {blockedReason && <Typography variant="body2" color="text.secondary">{blockedReason}</Typography>}
            </Box>

            {done && <Alert severity="success">Posted. {renderSummary?.(done)}</Alert>}
            {shown && !done && (
                <Alert severity={shown.errorCount > 0 ? 'error' : 'info'}>
                    {shown.errorCount > 0
                        ? `${shown.errorCount} error(s) - fix the file and preview again. Nothing is posted until there are none.`
                        : 'No errors. Review the preview, then post.'}
                    {renderSummary && <Box sx={{ mt: 0.5 }}>{renderSummary(shown)}</Box>}
                </Alert>
            )}
            {shown?.errors && shown.errors.length > 0 && (
                <Alert severity="error">
                    {shown.errors.map((e, i) => <div key={i}>{e}</div>)}
                </Alert>
            )}
            {shown?.warnings && shown.warnings.length > 0 && (
                <Alert severity="warning">
                    {shown.warnings.map((e, i) => <div key={i}>{e}</div>)}
                </Alert>
            )}
            {shown && shown.rows.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                    <Table data-help-section="accounting.openingImport.table" size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="accounting.openingImport.col.row">Row</TableCell>
                                <TableCell data-help="accounting.openingImport.col.status">Status</TableCell>
                                <TableCell data-help="accounting.openingImport.col.code">Code / Document</TableCell>
                                <TableCell data-help="accounting.openingImport.col.name">Name</TableCell>
                                {amountColumns === 'debitCredit' && <TableCell data-help="accounting.openingImport.col.debit" align="right">Debit</TableCell>}
                                {amountColumns === 'debitCredit' && <TableCell data-help="accounting.openingImport.col.credit" align="right">Credit</TableCell>}
                                {amountColumns === 'amount' && <TableCell data-help="accounting.openingImport.col.amount" align="right">Amount</TableCell>}
                                <TableCell data-help="accounting.openingImport.col.messages">Messages</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {shown.rows.map(r => (
                                <TableRow key={r.rowNumber} data-testid={`preview-row-${r.rowNumber}`}>
                                    <TableCell>{r.rowNumber}</TableCell>
                                    <TableCell><Chip size="small" label={r.status} color={statusColor(r.status)} /></TableCell>
                                    <TableCell>{r.code}</TableCell>
                                    <TableCell>{r.name}</TableCell>
                                    {amountColumns === 'debitCredit' && <TableCell align="right">{r.debit ? formatMoney(r.debit) : ''}</TableCell>}
                                    {amountColumns === 'debitCredit' && <TableCell align="right">{r.credit ? formatMoney(r.credit) : ''}</TableCell>}
                                    {amountColumns === 'amount' && <TableCell align="right">{r.debit != null ? formatMoney(r.debit) : ''}</TableCell>}
                                    <TableCell>
                                        {r.errors.map((m, i) => <Typography key={`e${i}`} variant="body2" color="error">{m}</Typography>)}
                                        {r.warnings.map((m, i) => <Typography key={`w${i}`} variant="body2" color="text.secondary">{m}</Typography>)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog data-help-page="accounting.openingImport.confirm.page" open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>{postLabel}</DialogTitle>
                <DialogContent><DialogContentText>{confirmText}</DialogContentText></DialogContent>
                <DialogActions>
                    <Button data-help="accounting.openingImport.confirm.cancel" onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button data-help="accounting.openingImport.confirm.ok" variant="contained" color="success" onClick={commit}>Confirm</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default OpeningImportPanel;
